import { Cake, FileText, MapPin } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Pessoa } from '@social/shared';
import { Etiqueta } from '../componentes/ui';
import {
  documentoDe,
  ehFisica,
  idadeDe,
  localDe,
  nomeDe,
  qualificacaoDe,
} from './pessoa-comum';

/**
 * O card da pessoa.
 *
 * Mesma divisão em andares do `CartaoSociedade` — identificação, de quem é,
 * fecho —, porque as duas consultas convivem no mesmo produto e o olho não
 * deve ter de reaprender onde as coisas ficam:
 *
 * 1. **Identificação** — o nome é a manchete; a natureza é etiqueta ao lado.
 * 2. **De quem é** — onde mora e, para pessoa física, a idade. Idade importa
 *    aqui: menor de 16 não pode ser sócio, e entre 16 e 18 só assistido (art.
 *    974 do Código Civil) — é o dado que decide se aquela pessoa serve.
 * 3. **Fecho** — o documento, que é como se confere se é a pessoa certa.
 *
 * Não há link: pessoa ainda não tem tela de detalhe, e um nome que parece
 * clicável e não é seria promessa falsa (ver a regra 6 da `social-desenho`).
 */
export function CartaoPessoa({ pessoa: p, acoes }: { pessoa: Pessoa; acoes: ReactNode }) {
  const idade = idadeDe(p);

  return (
    <article className="border-tinta-100 bg-superficie hover:border-marca-400 hover:bg-marca-600/[0.04] flex h-full flex-col gap-2 rounded-xl border p-3 transition-all hover:shadow-md sm:gap-3 sm:p-4 sm:hover:-translate-y-0.5">
      <header className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-tinta-900 truncate font-semibold">{nomeDe(p)}</p>
          <p className="text-tinta-500 truncate text-xs">{qualificacaoDe(p)}</p>
        </div>
        {acoes && <div className="-mt-1 -mr-1 shrink-0">{acoes}</div>}
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {/* `neutro` nos dois: PF/PJ é classificação, não estado — e cor com
            significado fica reservada para o que exige atenção (a etiqueta de
            idade abaixo). Ver "Cor com significado" na skill social-front. */}
        <Etiqueta>{ehFisica(p) ? 'PF' : 'PJ'}</Etiqueta>
        {/* Só quando a idade impede ou condiciona a participação — ver o
            cabeçalho. Etiqueta permanente de "maior de idade" em todo mundo
            seria ruído. */}
        {idade !== undefined && idade < 18 && (
          <Etiqueta tom="erro">
            {idade < 16 ? 'Menor de 16' : 'Menor — exige assistência'}
          </Etiqueta>
        )}
      </div>

      <dl className="text-tinta-500 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:gap-x-4">
        <div className="flex items-center gap-1.5">
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <dt className="sr-only">Município</dt>
          <dd className="truncate">{localDe(p)}</dd>
        </div>
        {idade !== undefined && (
          <div className="flex items-center gap-1.5">
            <Cake className="size-3.5 shrink-0" aria-hidden />
            <dt className="sr-only">Idade</dt>
            <dd className="tabular-nums">{idade} anos</dd>
          </div>
        )}
      </dl>

      <footer className="border-tinta-100 mt-auto flex items-end justify-between gap-3 border-t pt-2 sm:pt-3">
        <span className="text-tinta-500 flex items-center gap-1.5 text-xs">
          <FileText className="size-3.5 shrink-0" aria-hidden />
          <span className="tabular-nums">{documentoDe(p)}</span>
        </span>
        <span className="text-tinta-500 text-xs tabular-nums">#{p.codigo}</span>
      </footer>
    </article>
  );
}
